/**
 * Sessão do usuário (login, operadora escolhida, saída) — Nexora Grid (frontend).
 *
 * Criado em: 16/10/2026 · Última modificação: 02/11/2026 · Versão: 1.2.0
 *
 * Login da plataforma (US-201): sessão única no backend; aqui o token fica só nesta aba (sessionStorage) e a tela
 * volta ao login quando ele expira ou quando o backend responde 401 (por exemplo, login em outro lugar).
 * US-1801: ao abrir a tela com uma sessão guardada, os dados e as capacidades do usuário são relidos em `/auth/eu`
 * (permissões alteradas valem sem novo login).
 * v1.2: `atualizarUsuario` — depois de trocar a senha provisória, a sessão segue com o usuário sem o aviso.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, definirToken, guardarSessao, lerSessao, type Sessao, type Usuario } from "./api";

type Contexto = {
  usuario: Usuario | null;
  empresa: string | null;
  escolherEmpresa: (e: string) => void;
  entrar: (login: string, senha: string) => Promise<void>;
  sair: (motivo?: string) => Promise<void>;
  aviso: string | null;
  /** Troca o usuário da sessão (ex.: depois de trocar a senha provisória), sem novo login. */
  atualizarUsuario: (usuario: Usuario) => void;
};

const ContextoSessao = createContext<Contexto | null>(null);

function primeiraEmpresa(usuario: Usuario): string | null {
  return usuario.empresa ?? usuario.empresas.find((e) => e !== "*")
    ?? (usuario.empresas.includes("*") ? Object.keys(usuario.nomes_empresas ?? {}).sort()[0] : undefined) ?? null;
}

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [sessao, setSessao] = useState<Sessao | null>(lerSessao);
  const [empresa, setEmpresa] = useState<string | null>(() => sessao ? primeiraEmpresa(sessao.usuario) : null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [tokenValidado, setTokenValidado] = useState<string | null>(null);
  const [erroValidacao, setErroValidacao] = useState(false);

  const encerrar = useCallback((motivo?: string) => {
    definirToken(null);
    guardarSessao(null);
    setSessao(null);
    setTokenValidado(null);
    setErroValidacao(false);
    setAviso(motivo ?? null);
  }, []);

  useEffect(() => {
    const expirou = () => encerrar("Sessão encerrada (expirou ou houve login em outro lugar). Entre de novo.");
    window.addEventListener("nexora:sessao-expirada", expirou);
    if (!sessao) return () => window.removeEventListener("nexora:sessao-expirada", expirou);
    const falta = new Date(sessao.expiraEm).getTime() - Date.now();
    const relogio = window.setTimeout(expirou, Math.max(0, falta));
    return () => { window.removeEventListener("nexora:sessao-expirada", expirou); window.clearTimeout(relogio); };
  }, [sessao, encerrar]);

  // US-1801: relê o usuário (papéis e capacidades da matriz) ao abrir a tela com sessão guardada
  const token = sessao?.token;
  useEffect(() => {
    if (!token) return;
    let vivo = true;
    let sequencia = 0;
    const atualizar = () => {
      const pedido = ++sequencia;
      return api<Usuario>("/v1/auth/eu").then((usuario) => {
      if (!vivo || pedido !== sequencia) return;
      setSessao((atual) => {
        if (!atual || atual.token !== token) return atual;
        if (JSON.stringify(atual.usuario) === JSON.stringify(usuario)) return atual;
        const nova = { ...atual, usuario };
        guardarSessao(nova);
        return nova;
      });
      setEmpresa((atual) => atual && (usuario.empresas.includes("*") || usuario.empresas.includes(atual))
        ? atual : primeiraEmpresa(usuario));
      setTokenValidado(token);
      setErroValidacao(false);
    }).catch(() => {
      if (vivo && pedido === sequencia) { setTokenValidado(null); setErroValidacao(true); }
    });
    };
    void atualizar();
    const intervalo = window.setInterval(() => void atualizar(), 30000);
    const aoFocar = () => void atualizar();
    window.addEventListener("focus", aoFocar);
    return () => { vivo = false; window.clearInterval(intervalo); window.removeEventListener("focus", aoFocar); };
  }, [token]);

  const entrar = useCallback(async (login: string, senha: string) => {
    const r = await api<{ access_token: string; expira_em: string; usuario: Usuario }>("/v1/auth/login",
      { corpo: { login, senha } });
    const nova = { token: r.access_token, usuario: r.usuario, expiraEm: r.expira_em };
    definirToken(nova.token);
    guardarSessao(nova);
    setTokenValidado(nova.token);
    setErroValidacao(false);
    setSessao(nova);
    setEmpresa(primeiraEmpresa(r.usuario));
    setAviso(null);
  }, []);

  const sair = useCallback(async (motivo?: string) => {
    // limpa a aba primeiro (sair é imediato) e avisa o backend em seguida, mesmo se a página for fechada
    const token = sessao?.token;
    encerrar(motivo);
    if (token) {
      await fetch("/api/v1/auth/logout", { method: "POST", headers: { Authorization: `Bearer ${token}` }, keepalive: true })
        .catch(() => undefined);
    }
  }, [encerrar, sessao]);

  const atualizarUsuario = useCallback((usuario: Usuario) => {
    setSessao((atual) => {
      if (!atual) return atual;
      const nova = { ...atual, usuario };
      guardarSessao(nova);
      return nova;
    });
  }, []);

  const valor = useMemo<Contexto>(() => ({ usuario: sessao?.usuario ?? null, empresa, escolherEmpresa: setEmpresa,
    entrar, sair, aviso, atualizarUsuario }), [sessao, empresa, entrar, sair, aviso, atualizarUsuario]);
  const paginaPublica = ["/cadastrar-aparelho", "/confirmar-acao", "/redefinir-senha", "/confirmar-cadastro"].includes(window.location.pathname);
  if (sessao && tokenValidado !== sessao.token && !paginaPublica) return <main className="pagina">
    <p role="status">{erroValidacao ? "Não foi possível confirmar seu acesso. Tente novamente." : "Confirmando acesso…"}</p>
    {erroValidacao && <><button type="button" className="nx-botao" onClick={() => window.dispatchEvent(new Event("focus"))}>Tentar novamente</button>
      <button type="button" className="nx-botao" onClick={() => void sair()}>Sair</button></>}
  </main>;
  return <ContextoSessao.Provider value={valor}>{children}</ContextoSessao.Provider>;
}

export function useSessao(): Contexto {
  const c = useContext(ContextoSessao);
  if (!c) throw new Error("useSessao fora do ProvedorSessao");
  return c;
}
